/*
  This example requires some changes to your config:

  ```
  // tailwind.config.js
  module.exports = {
    // ...
    plugins: [
      // ...
      require('@tailwindcss/forms'),
    ],
  }
  ```
*/
import React, {Fragment, useEffect, useRef, useState} from 'react'
import {Dialog, Transition} from '@headlessui/react'
import {ArrowUturnLeftIcon, CheckCircleIcon, CheckIcon, ExclamationTriangleIcon, GiftIcon, XMarkIcon} from '@heroicons/react/24/outline'
import {getVenueSession} from "../../requests/AppSession";
import {useNavigate} from "react-router-dom";
import LoginError from "../login/LoginError";
import CircularProgress from "@mui/material/CircularProgress";
import VTSellQRReader from "./VTSellQRReader";
import Header from "../header/Header";
import {TierGridSkeleton} from "./VTSkeletons";
import sound from "./sound/cash2.mp3"
import soundError from "./sound/error.mp3"
import Footer from "../footer/Footer";
import VTCheckRewardsQRReader from "./VTCheckRewardsQRReader";
import {useAuth0} from "@auth0/auth0-react";
import {getUserVenueRewards} from "../requests/AppRequestReward";
import {findVenueSellPackages} from "../requests/AppRequestPackages";
import {giveVT, revertVT} from "../requests/AppRequestVenue";
import VTRevertSellQRReader from "./VTRevertSellQRReader";

export default function VTSell() {
    let navigate = useNavigate();

    const {isLoading, getAccessTokenSilently} = useAuth0();

    const [open, setOpen] = useState(false);
    const [revertOpen, setRevertOpen] = useState(false);
    const [rewardOpen, setRewardOpen] = useState(false);

    const [show, setShow] = useState(false);
    const [showSuccessMessage, setShowSuccessMessage] = useState(null);
    const [showSuccessSubMessage, setShowSuccessSubMessage] = useState(null);

    const [showError, setShowError] = useState(false);
    const [showErrorMessage, setShowErrorMessage] = useState(null);

    const [isQRReader, setIsQRReader] = useState(false);
    const [isTypeIn, setIsTypeIn] = useState(false);

    const [showQRReader, setShowQRReader] = useState(false);
    const [showRevertQRReader, setShowRevertQRReader] = useState(false);

    const [showCheckRewardQRReader, setShowCheckRewardQRReader] = useState(false);

    const [openConfirmRevert, setConfirmRevertOpen] = useState(false);
    const cancelButtonRef = useRef(null);

    const [tiers, setTiers] = useState(null);
    const [selectedSize, setSelectedSize] = useState(null);
    const [userCode, setUserCode] = useState(null);
    const [adminCode, setAdminCode] = useState(null);
    const [venueId, setVenueId] = React.useState(null);
    const [venueName, setVenueName] = React.useState(null);
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const [errorMessage, setErrorMessage] = React.useState(null);
    const [adminCodeFeatureFlag, setAdminCodeFeatureFlag] = React.useState(null);
    const [revertAmount, setRevertAmount] = React.useState(null);

    const [userReward, setUserReward] = React.useState(null);

    const [venueRewardsFeatureFlag, setVenueRewardsFeatureFlag] = React.useState(false);
    const [venueStripePaymentsFeatureFlag, setVenueStripePaymentsFeatureFlag] = React.useState(false);

    useEffect(() => {
        getVenueSession().then(venueSession => {
            if (venueSession && venueSession.id !== "null") {
                setVenueId(venueSession.venueId);
                setVenueName(venueSession.venueName);
                setIsQRReader(venueSession.venueScanCustomerCodePaymentsFeatureFlag);
                setIsTypeIn(venueSession.venueInputCustomerCodePaymentsFeatureFlag);
            } else {
                navigate('/login/mobile', {replace: true});
            }

            if (venueSession) {
                setVenueRewardsFeatureFlag(venueSession.venueRewardsFeatureFlag);
            } else {
                setVenueRewardsFeatureFlag(false);
            }
        })
        return function cleanup() {

        };
    }, []);

    const getSuccess = async responseJson => {
        if (
            responseJson &&
            responseJson.packagesVTList
        ) {
            setTiers(responseJson.packagesVTList);
            if (responseJson.adminCodeFeatureFlag) {
                setAdminCodeFeatureFlag(true);
            } else {
                setAdminCodeFeatureFlag(false);
                setAdminCode("10756845868867801");
            }
        } else {
            setErrorMessage("Error accessing venue packages, please refresh and verify Internet Connection.");
        }
    };

    const getError = forceLogout => {
        setErrorMessage("Error accessing venue packages, please refresh and verify Internet Connection.");
    };

    useEffect(() => {
        if (!isLoading && venueId !== null) {
            findVenueSellPackages(venueId, getSuccess, getError, getAccessTokenSilently);
        }
        return function cleanup() {

        };
    }, [isLoading, venueId]);

    const submitSuccess = async responseJson => {
        setIsSubmitting(false);
        console.log(responseJson);
        if (
            responseJson &&
            responseJson.vtLeft >= 0
        ) {
            // setAdminCode(null);
            setShow(true);
            setShowSuccessMessage("Successfully added " + selectedSize?.virtualTips + " VirtualTipz!");
            setShowSuccessSubMessage("Customer now has " + responseJson.vtLeft + " VirtualTipz");
            setUserCode(null);
            setRevertAmount(null);
            setSelectedSize(null);
            setOpen(false);
            setRevertOpen(false);
            setShowQRReader(false);
            setShowRevertQRReader(false);
            setTimeout(() => {
                setShow(false);
            }, 10000);
            await new Audio(sound).play();
        } else {
            setShow(false);
            setShowQRReader(false);
            setShowRevertQRReader(false);
            setErrorMessage("There was a problem assigning the VT to the User. Please verify User Code is correct.");
            setShowError(true);
            setShowErrorMessage("Failed to add VirtualTipz, try again!");
            setTimeout(() => {
                setShowError(false);
            }, 10000);
            await new Audio(soundError).play();
        }
    };

    const submitError = forceLogout => {
        setShow(false);
        setShowQRReader(false);
        setShowRevertQRReader(false);
        setIsSubmitting(false);
        setErrorMessage("There was a problem assigning the VT to the User. Please verify User Code is correct.");
        setShowError(true);
        setShowErrorMessage("Failed to add VirtualTipz, try again!");
        setTimeout(() => {
            setShowError(false);
        }, 10000);
        new Audio(soundError).play();
    };

    const submitRevertSuccess = async responseJson => {
        setIsSubmitting(false);
        setConfirmRevertOpen(false);
        console.log(responseJson);
        if (
            responseJson &&
            responseJson.vtLeft >= 0
        ) {
            // setAdminCode(null);
            setUserCode(null);
            setShowSuccessMessage("Successfully reverted " + revertAmount + " VirtualTipz!");
            setRevertAmount(null);
            setSelectedSize(null);
            setOpen(false);
            setRevertOpen(false);
            setShow(true);
            setTimeout(() => {
                setShow(false);
            }, 10000);
        } else {
            setErrorMessage("There was a problem reverting the VT from the User. Please verify User Code is correct.");
            // setShowError(true);
            // setShowErrorMessage("Failed to revert VirtualTipz, try again!");
        }
    };

    const submitRevertError = forceLogout => {
        setIsSubmitting(false);
        setConfirmRevertOpen(false);
        setErrorMessage("There was a problem reverting the VT from the User. Please verify User Code is correct.");
        // setShowError(true);
        // setShowErrorMessage("Failed to revert VirtualTipz, try again!");
    };

    const submitVT = (selectedSize, userCode) => {
        if (!userCode || userCode.trim() === "" || !adminCode || adminCode.trim() === "") {
            setErrorMessage("There was a problem assigning the VT to the User. Please verify User Code is correct.");
            setShowError(true);
            setShowErrorMessage("Failed to add VirtualTipz, try again!");
            setTimeout(() => {
                setShowError(false);
            }, 10000);
            new Audio(soundError).play();
            return;
        }
        setErrorMessage(null);
        setIsSubmitting(true);
        if (selectedSize) {
            giveVT(venueId, userCode, adminCode, selectedSize.id, submitSuccess, submitError, getAccessTokenSilently);
        } else {
            setIsSubmitting(false);
        }
    };

    const getRewardsSuccess = async responseJson => {
        setShowCheckRewardQRReader(false);
        if (
            responseJson
        ) {
            setUserReward(responseJson);
            setRewardOpen(true);
        } else {
            setErrorMessage("There was a checking User Rewards. Please verify User Code is correct.");
        }
    };

    const getRewardsError = forceLogout => {
        setShowCheckRewardQRReader(false);
        setErrorMessage("There was a checking User Rewards. Please verify User Code is correct.");
    };

    const submitCheckReward = (selectedSize, userCode) => {
        if (!userCode || userCode.trim() === "" || !adminCode || adminCode.trim() === "") {
            setErrorMessage("There was a checking User Rewards. Please verify User Code is correct.");
            setShowError(true);
            setShowErrorMessage("Failed to check User Rewards, try again!");
            setTimeout(() => {
                setShowError(false);
            }, 10000);
            new Audio(soundError).play();
            return;
        }
        setErrorMessage(null);
        setIsSubmitting(true);
        setUserReward(null);
        getUserVenueRewards(venueId, userCode, getRewardsSuccess, getRewardsError, getAccessTokenSilently);
    };

    const submitRevertVT = (selectedSize, userCode) => {
        if (!userCode || userCode.trim() === "" || !adminCode || adminCode.trim() === "") {
            setErrorMessage("There was a problem reverting the VT. Please try again.");
            setShowError(true);
            setShowErrorMessage("Failed to revert VirtualTipz, try again!");
            setTimeout(() => {
                setShowError(false);
            }, 10000);
            new Audio(soundError).play();
            return;
        }
        setErrorMessage(null);
        setIsSubmitting(true);
        setRevertAmount(null);
        revertVT(venueId, userCode, adminCode, revertAmount, submitRevertSuccess, submitRevertError, getAccessTokenSilently);
    };

    return (
        <>
            {
                showQRReader ?
                    <VTSellQRReader setIsQRReader={setIsQRReader} submitVT={submitVT} selectedSize={selectedSize}
                                    setShowQRReader={setShowQRReader}/> :
                    showCheckRewardQRReader ?
                        <VTCheckRewardsQRReader setIsQRReader={setIsQRReader} submitCheckReward={submitCheckReward}
                                                setShowCheckRewardQRReader={setShowCheckRewardQRReader}/> :
                        showRevertQRReader ?
                            <VTRevertSellQRReader setIsQRReader={setIsQRReader} submitRevertVT={submitRevertVT}
                                                  setShowRevertQRReader={setShowRevertQRReader}
                                                  revertAmount={revertAmount}/> :
                            <>
                                <Header headerType="AdminSellOnly"/>
                                <div className="bg-white dark:bg-gray-900">

                                    {/* Pricing with four tiers and toggle */}
                                    <div className="bg-gradient-to-b from-white to-gray-50 dark:from-gray-900 dark:to-gray-800">
                                        <div className="mx-auto max-w-7xl px-4 pt-12 sm:px-6 lg:px-8">
                                            <div className="flex flex-col items-center">
                                                <h1 className="text-4xl font-bold tracking-tight text-gray-900 dark:text-gray-100 text-center sm:text-5xl">Sell
                                                    Virtual<span
                                                        className="text-pink-400">Tipz</span></h1>

                                                {venueRewardsFeatureFlag && <button
                                                    className="mt-8 flex w-full max-w-xs items-center justify-center gap-2 rounded-lg bg-white dark:bg-gray-800 py-3 text-sm font-semibold text-pink-700 ring-1 ring-inset ring-pink-300 transition hover:bg-pink-50 active:bg-pink-100"
                                                    onClick={(event) => {
                                                        setShowCheckRewardQRReader(true);
                                                    }}
                                                >
                                                    <GiftIcon className="h-5 w-5" aria-hidden="true"/>
                                                    Check User Rewards
                                                </button>}

                                                {!tiers && <TierGridSkeleton/>}
                                            </div>
                                            <div
                                                className="mt-12 space-y-4 sm:mt-16 sm:grid sm:grid-cols-2 sm:gap-6 sm:space-y-0 lg:mx-auto lg:max-w-4xl xl:mx-0 xl:max-w-none xl:grid-cols-4">
                                                {tiers && tiers.map((tier) => (
                                                    <div key={tier.id}
                                                         className="divide-y divide-gray-200 dark:divide-gray-700 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
                                                        <div className="p-6">
                                                            <h2 className="text-lg font-medium leading-6 text-gray-900 dark:text-gray-100">{tier.name} ({tier.virtualTips} VirtualTipz)</h2>
                                                            <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">{tier.description}</p>
                                                            <p className="mt-8">
                                        <span
                                            className="text-4xl font-bold tracking-tight text-gray-900 dark:text-gray-100">${tier.price}</span>{' '}
                                                                {/*<span className="text-base font-medium text-gray-500">/mo</span>*/}
                                                            </p>
                                                            {isQRReader && <button
                                                                name={tier.id}
                                                                className="mt-8 block w-full rounded-md border border-transparent bg-pink-600 py-2 text-center text-sm font-semibold text-white hover:bg-pink-700"
                                                                onClick={(event) => {
                                                                    setSelectedSize(tiers.find(t => t.id === event.target.name));
                                                                    setShowQRReader(true);
                                                                }}
                                                            >
                                                                Sell {tier.name} (Scanner)
                                                            </button>}
                                                            {isTypeIn && <button
                                                                name={tier.id}
                                                                className="mt-4 block w-full rounded-md border border-transparent bg-pink-600 py-2 text-center text-sm font-semibold text-white hover:bg-pink-700"
                                                                onClick={(event) => {
                                                                    setSelectedSize(tiers.find(t => t.id === event.target.name));
                                                                    setOpen(true);
                                                                }}
                                                            >
                                                                Sell {tier.name} (Type In)
                                                            </button>}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                            {tiers && <div className="mx-auto mt-10 mb-6 w-full max-w-xs">
                                                <div className="flex items-center gap-3">
                                                    <div className="h-px flex-1 bg-gray-200 dark:bg-gray-700"/>
                                                    <span className="text-xs font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500">or</span>
                                                    <div className="h-px flex-1 bg-gray-200 dark:bg-gray-700"/>
                                                </div>
                                                <button
                                                    className="mt-4 flex w-full items-center justify-center gap-2 rounded-lg bg-white dark:bg-gray-800 py-3 text-sm font-semibold text-red-700 ring-1 ring-inset ring-red-300 transition hover:bg-red-50 active:bg-red-100"
                                                    onClick={(event) => {
                                                        setRevertOpen(true);
                                                    }}
                                                >
                                                    <ArrowUturnLeftIcon className="h-5 w-5" aria-hidden="true"/>
                                                    Revert VirtualTipz
                                                </button>
                                            </div>}
                                        </div>
                                    </div>
                                </div>
                                <Footer/>
                            </>
            }


            <Transition.Root show={open} as={Fragment}>
                <Dialog as="div" className="relative z-10" onClose={setOpen}>
                    <div className="fixed inset-0"/>

                    <div className="fixed inset-0 overflow-hidden">
                        <div className="absolute inset-0 overflow-hidden">
                            <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10 sm:pl-16">
                                <Transition.Child
                                    as={Fragment}
                                    enter="transform transition ease-in-out duration-500 sm:duration-700"
                                    enterFrom="translate-x-full"
                                    enterTo="translate-x-0"
                                    leave="transform transition ease-in-out duration-500 sm:duration-700"
                                    leaveFrom="translate-x-0"
                                    leaveTo="translate-x-full"
                                >
                                    <Dialog.Panel className="pointer-events-auto w-screen max-w-md">
                                        <div
                                            className="flex h-full flex-col divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-800 shadow-xl">
                                            <div className="h-0 flex-1 overflow-y-auto">
                                                <div className="bg-pink-700 px-4 py-6 sm:px-6">
                                                    <div className="flex items-center justify-between">
                                                        <Dialog.Title
                                                            className="text-base font-semibold leading-6 text-white">
                                                            {selectedSize && selectedSize.name} ${selectedSize && selectedSize.price}
                                                        </Dialog.Title>
                                                        <div className="ml-3 flex h-7 items-center">
                                                            <button
                                                                type="button"
                                                                className="rounded-md bg-pink-700 text-pink-200 hover:text-white focus:outline-none focus:ring-2 focus:ring-white"
                                                                onClick={() => {
                                                                    setSelectedSize(null);
                                                                    // setAdminCode(null);
                                                                    setUserCode(null);
                                                                    setOpen(false);
                                                                }}
                                                            >
                                                                <span className="sr-only">Close panel</span>
                                                                <XMarkIcon className="h-6 w-6" aria-hidden="true"/>
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="flex flex-1 flex-col justify-between">
                                                    <div className="divide-y divide-gray-200 dark:divide-gray-700 px-4 sm:px-6">
                                                        <div className="space-y-6 pb-5 pt-6">
                                                            {errorMessage && <LoginError errorMessage={errorMessage}/>}
                                                            <div>
                                                                <label
                                                                    htmlFor="project-name"
                                                                    className="block text-sm font-medium leading-6 text-gray-900 dark:text-gray-100"
                                                                >
                                                                    User Code
                                                                </label>
                                                                <div className="mt-2">
                                                                    <input
                                                                        type="text"
                                                                        name="userCode"
                                                                        value={userCode ?? ""}
                                                                        className="block w-full rounded-md border-0 py-1.5 text-gray-900 dark:text-gray-100 dark:bg-gray-800 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-pink-600 sm:text-sm sm:leading-6"
                                                                        onChange={(event) => {
                                                                            let uc = event.target.value;
                                                                            if (uc) {
                                                                                uc = uc.toUpperCase();
                                                                            }
                                                                            setUserCode(uc);
                                                                        }}
                                                                    />
                                                                </div>
                                                            </div>
                                                            {adminCodeFeatureFlag && <div>
                                                                <label
                                                                    htmlFor="project-name"
                                                                    className="block text-sm font-medium leading-6 text-gray-900 dark:text-gray-100"
                                                                >
                                                                    Admin Code
                                                                </label>
                                                                <div className="mt-2">
                                                                    <input
                                                                        type="text"
                                                                        name="adminCode"
                                                                        className="block w-full rounded-md border-0 py-1.5 text-gray-900 dark:text-gray-100 dark:bg-gray-800 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-pink-600 sm:text-sm sm:leading-6"
                                                                        onChange={(event) => {
                                                                            setAdminCode(event.target.value);
                                                                        }}
                                                                    />
                                                                </div>
                                                            </div>}
                                                        </div>
                                                        <div className="pb-6 pt-4">
                                                            <div className="flex text-sm">
                                                                <div
                                                                    className="group inline-flex items-center font-medium text-pink-600 hover:text-pink-900"
                                                                >
                                                                    {/*<LinkIcon*/}
                                                                    {/*    className="h-5 w-5 text-pink-500 group-hover:text-pink-900"*/}
                                                                    {/*    aria-hidden="true"*/}
                                                                    {/*/>*/}
                                                                    <span
                                                                        className="ml-2">{selectedSize && selectedSize.virtualTips} VirtualTipz</span>
                                                                    <span
                                                                        className="ml-2">${selectedSize && selectedSize.price}</span>
                                                                </div>
                                                            </div>
                                                            <div className="mt-4 flex text-sm">
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="flex flex-shrink-0 justify-end px-4 py-4">
                                                <button
                                                    type="button"
                                                    className="rounded-md bg-white dark:bg-gray-800 px-3 py-2 text-sm font-semibold text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-800"
                                                    onClick={() => {
                                                        setSelectedSize(null);
                                                        // setAdminCode(null);
                                                        setUserCode(null);
                                                        setOpen(false)
                                                    }}
                                                >
                                                    Cancel
                                                </button>
                                                <button
                                                    disabled={isSubmitting}
                                                    className="ml-4 inline-flex justify-center rounded-md bg-pink-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-pink-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-pink-600"
                                                    onClick={() => {
                                                        if (!userCode || userCode.trim() === "" || !adminCode || adminCode.trim() === "") {
                                                            setErrorMessage("There was a problem assigning the VT to the User. Please verify User Code is correct.");
                                                            return;
                                                        }

                                                        submitVT(selectedSize, userCode);

                                                        // if (!userCode || userCode.trim() === "" || !adminCode || adminCode.trim() === "") {
                                                        //     setErrorMessage("There was a problem assigning the VT to the User. Please verify User Code is correct.");
                                                        //     return;
                                                        // }
                                                        // setErrorMessage(null);
                                                        // setIsSubmitting(true);
                                                        // if (selectedSize) {
                                                        //     giveVT(venueId, userCode, adminCode, selectedSize.id, submitSuccess, submitError);
                                                        // } else {
                                                        //     setIsSubmitting(false);
                                                        // }
                                                    }}
                                                >
                                                    Add VT to User {isSubmitting &&
                                                    <CircularProgress color="inherit" size={20}/>}
                                                </button>
                                            </div>
                                        </div>
                                    </Dialog.Panel>
                                </Transition.Child>
                            </div>
                        </div>
                    </div>
                </Dialog>
            </Transition.Root>

            <Transition.Root show={revertOpen} as={Fragment}>
                <Dialog as="div" className="relative z-10" onClose={setRevertOpen}>
                    <div className="fixed inset-0"/>

                    <div className="fixed inset-0 overflow-hidden">
                        <div className="absolute inset-0 overflow-hidden">
                            <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10 sm:pl-16">
                                <Transition.Child
                                    as={Fragment}
                                    enter="transform transition ease-in-out duration-500 sm:duration-700"
                                    enterFrom="translate-x-full"
                                    enterTo="translate-x-0"
                                    leave="transform transition ease-in-out duration-500 sm:duration-700"
                                    leaveFrom="translate-x-0"
                                    leaveTo="translate-x-full"
                                >
                                    <Dialog.Panel className="pointer-events-auto w-screen max-w-md">
                                        <div
                                            className="flex h-full flex-col divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-800 shadow-xl">
                                            <div className="h-0 flex-1 overflow-y-auto">
                                                <div className="bg-pink-700 px-4 py-6 sm:px-6">
                                                    <div className="flex items-center justify-between">
                                                        <Dialog.Title
                                                            className="text-base font-semibold leading-6 text-white">
                                                            Revert VirtualTipz
                                                        </Dialog.Title>
                                                        <div className="ml-3 flex h-7 items-center">
                                                            <button
                                                                type="button"
                                                                className="rounded-md bg-pink-700 text-pink-200 hover:text-white focus:outline-none focus:ring-2 focus:ring-white"
                                                                onClick={() => {
                                                                    // setAdminCode(null);
                                                                    setUserCode(null);
                                                                    setRevertAmount(null);
                                                                    setRevertOpen(false);
                                                                }}
                                                            >
                                                                <span className="sr-only">Close panel</span>
                                                                <XMarkIcon className="h-6 w-6" aria-hidden="true"/>
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="flex flex-1 flex-col justify-between">
                                                    <div className="divide-y divide-gray-200 dark:divide-gray-700 px-4 sm:px-6">
                                                        <div className="space-y-6 pb-5 pt-6">
                                                            {errorMessage && <LoginError errorMessage={errorMessage}/>}
                                                            {!isQRReader && <div>
                                                                <label
                                                                    htmlFor="project-name"
                                                                    className="block text-sm font-medium leading-6 text-gray-900 dark:text-gray-100"
                                                                >
                                                                    User Code
                                                                </label>
                                                                <div className="mt-2">
                                                                    <input
                                                                        type="text"
                                                                        name="userCode"
                                                                        value={userCode ?? ""}
                                                                        className="block w-full rounded-md border-0 py-1.5 text-gray-900 dark:text-gray-100 dark:bg-gray-800 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-pink-600 sm:text-sm sm:leading-6"
                                                                        onChange={(event) => {
                                                                            let uc = event.target.value;
                                                                            if (uc) {
                                                                                uc = uc.toUpperCase();
                                                                            }
                                                                            setUserCode(uc);
                                                                        }}
                                                                    />
                                                                </div>
                                                            </div>}
                                                            {adminCodeFeatureFlag && <div>
                                                                <label
                                                                    htmlFor="project-name"
                                                                    className="block text-sm font-medium leading-6 text-gray-900 dark:text-gray-100"
                                                                >
                                                                    Admin Code
                                                                </label>
                                                                <div className="mt-2">
                                                                    <input
                                                                        type="text"
                                                                        name="adminCode"
                                                                        className="block w-full rounded-md border-0 py-1.5 text-gray-900 dark:text-gray-100 dark:bg-gray-800 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-pink-600 sm:text-sm sm:leading-6"
                                                                        onChange={(event) => {
                                                                            setAdminCode(event.target.value);
                                                                        }}
                                                                    />
                                                                </div>
                                                            </div>}
                                                            <div>
                                                                <label
                                                                    htmlFor="project-name"
                                                                    className="block text-sm font-medium leading-6 text-gray-900 dark:text-gray-100"
                                                                >
                                                                    Revert VirtualTipz Amount
                                                                </label>
                                                                <div className="mt-2">
                                                                    <input
                                                                        type="number"
                                                                        name="revertAmount"
                                                                        className="block w-full rounded-md border-0 py-1.5 text-gray-900 dark:text-gray-100 dark:bg-gray-800 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-pink-600 sm:text-sm sm:leading-6"
                                                                        onChange={(event) => {
                                                                            setRevertAmount(event.target.value);
                                                                        }}
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="pb-6 pt-4">
                                                            <div className="flex text-sm">
                                                                <div
                                                                    className="group inline-flex items-center font-medium text-pink-600 hover:text-pink-900"
                                                                >

                                                                </div>
                                                            </div>
                                                            <div className="mt-4 flex text-sm">

                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="flex flex-shrink-0 justify-end px-4 py-4">
                                                <button
                                                    type="button"
                                                    className="rounded-md bg-white dark:bg-gray-800 px-3 py-2 text-sm font-semibold text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-800"
                                                    onClick={() => {
                                                        // setAdminCode(null);
                                                        setUserCode(null);
                                                        setRevertAmount(null);
                                                        setRevertOpen(false)
                                                    }}
                                                >
                                                    Cancel
                                                </button>
                                                <button
                                                    className="ml-4 inline-flex justify-center rounded-md bg-pink-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-pink-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-pink-600"
                                                    onClick={() => {
                                                        if (isQRReader) {
                                                            if (isNaN(revertAmount) || revertAmount <= 0) {
                                                                setErrorMessage("Revert amount must be greater than 0.");
                                                                return;
                                                            }
                                                            setErrorMessage(null);
                                                            setShowRevertQRReader(true);
                                                            setRevertOpen(false);
                                                        } else {
                                                            if (!userCode || userCode.trim() === "" || !adminCode || adminCode.trim() === "") {
                                                                setErrorMessage("There was a problem reverting the VT from the User. Please verify User Code is correct.");
                                                                return;
                                                            }
                                                            if (isNaN(revertAmount) || revertAmount <= 0) {
                                                                setErrorMessage("Revert amount must be greater than 0.");
                                                                return;
                                                            }
                                                            setErrorMessage(null);
                                                            setConfirmRevertOpen(true);
                                                        }
                                                    }}
                                                >
                                                    Revert VT from User
                                                </button>
                                            </div>
                                        </div>
                                    </Dialog.Panel>
                                </Transition.Child>
                            </div>
                        </div>
                    </div>
                </Dialog>
            </Transition.Root>

            <Transition.Root show={openConfirmRevert} as={Fragment}>
                <Dialog as="div" className="relative z-10" initialFocus={cancelButtonRef}
                        onClose={setConfirmRevertOpen}>
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"/>
                    </Transition.Child>

                    <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
                        <div
                            className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                            <Transition.Child
                                as={Fragment}
                                enter="ease-out duration-300"
                                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                                enterTo="opacity-100 translate-y-0 sm:scale-100"
                                leave="ease-in duration-200"
                                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            >
                                <Dialog.Panel
                                    className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
                                    <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                                        <div className="sm:flex sm:items-start">
                                            <div
                                                className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                                                <ExclamationTriangleIcon className="h-6 w-6 text-red-600"
                                                                         aria-hidden="true"/>
                                            </div>
                                            <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                                                <Dialog.Title as="h3"
                                                              className="text-base font-semibold leading-6 text-gray-900">
                                                    Revert VT
                                                </Dialog.Title>
                                                <div className="mt-2">
                                                    <p className="text-sm text-gray-500">
                                                        Are you sure you want revert VT from the user account?
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                                        <button
                                            disabled={isSubmitting}
                                            type="button"
                                            className="inline-flex w-full justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 sm:ml-3 sm:w-auto"
                                            onClick={() => {
                                                revertVT(venueId, userCode, adminCode, revertAmount, submitRevertSuccess, submitRevertError, getAccessTokenSilently);
                                                setIsSubmitting(true);
                                            }}
                                        >
                                            Revert {isSubmitting &&
                                            <CircularProgress color="inherit" size={20}/>}
                                        </button>
                                        <button
                                            type="button"
                                            className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                                            onClick={() => setConfirmRevertOpen(false)}
                                            ref={cancelButtonRef}
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </Dialog.Panel>
                            </Transition.Child>
                        </div>
                    </div>
                </Dialog>
            </Transition.Root>


            {/* Global notification live region, render this permanently at the end of the document */}
            <div
                aria-live="assertive"
                className="pointer-events-none fixed inset-0 flex items-end px-4 py-6 sm:items-start sm:p-6"
            >
                <div className="flex w-full flex-col items-center space-y-4 sm:items-end">
                    {/* Notification panel, dynamically insert this into the live region when it needs to be displayed */}
                    <Transition
                        show={show}
                        as={Fragment}
                        enter="transform ease-out duration-300 transition"
                        enterFrom="translate-y-2 opacity-0 sm:translate-y-0 sm:translate-x-2"
                        enterTo="translate-y-0 opacity-100 sm:translate-x-0"
                        leave="transition ease-in duration-100"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div
                            className="pointer-events-auto w-full max-w-sm overflow-hidden rounded-lg bg-white dark:bg-gray-800 shadow-lg ring-1 ring-black ring-opacity-5">
                            <div className="p-4">
                                <div className="flex items-start">
                                    <div className="flex-shrink-0">
                                        <CheckCircleIcon className="h-6 w-6 text-green-600" aria-hidden="true"/>
                                    </div>
                                    <div className="ml-3 w-0 flex-1 pt-0.5">
                                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{showSuccessMessage}</p>
                                        {/*<p className="mt-1 text-sm text-gray-500">{addedVTAmount} VirtualTipz</p>*/}
                                        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{showSuccessSubMessage}</p>
                                    </div>
                                    <div className="ml-4 flex flex-shrink-0">
                                        <button
                                            type="button"
                                            className="inline-flex rounded-md bg-white dark:bg-gray-800 text-gray-400 dark:text-gray-500 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-offset-2"
                                            onClick={() => {
                                                setShow(false);
                                            }}
                                        >
                                            <span className="sr-only">Close</span>
                                            <XMarkIcon className="h-5 w-5" aria-hidden="true"/>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Transition>
                </div>
            </div>

            {/* Global notification live region, render this permanently at the end of the document */}
            <div
                aria-live="assertive"
                className="pointer-events-none fixed inset-0 flex items-end px-4 py-6 sm:items-start sm:p-6"
            >
                <div className="flex w-full flex-col items-center space-y-4 sm:items-end">
                    {/* Notification panel, dynamically insert this into the live region when it needs to be displayed */}
                    <Transition
                        show={showError}
                        as={Fragment}
                        enter="transform ease-out duration-300 transition"
                        enterFrom="translate-y-2 opacity-0 sm:translate-y-0 sm:translate-x-2"
                        enterTo="translate-y-0 opacity-100 sm:translate-x-0"
                        leave="transition ease-in duration-100"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div
                            className="pointer-events-auto w-full max-w-sm overflow-hidden rounded-lg bg-white dark:bg-gray-800 shadow-lg ring-1 ring-black ring-opacity-5">
                            <div className="p-4">
                                <div className="flex items-start">
                                    <div className="flex-shrink-0">
                                        <CheckCircleIcon className="h-6 w-6 text-red-600" aria-hidden="true"/>
                                    </div>
                                    <div className="ml-3 w-0 flex-1 pt-0.5">
                                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{showErrorMessage}</p>
                                        {/*<p className="mt-1 text-sm text-gray-500">{selectedSize && selectedSize.virtualTips} VirtualTipz</p>*/}
                                    </div>
                                    <div className="ml-4 flex flex-shrink-0">
                                        <button
                                            type="button"
                                            className="inline-flex rounded-md bg-white dark:bg-gray-800 text-gray-400 dark:text-gray-500 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-offset-2"
                                            onClick={() => {
                                                setShowError(false);
                                                setErrorMessage(false);
                                            }}
                                        >
                                            <span className="sr-only">Close</span>
                                            <XMarkIcon className="h-5 w-5" aria-hidden="true"/>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Transition>
                </div>
            </div>

            <Transition.Root show={rewardOpen} as={Fragment}>
                <Dialog as="div" className="relative z-10" onClose={setRewardOpen}>
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"/>
                    </Transition.Child>

                    <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
                        <div
                            className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                            <Transition.Child
                                as={Fragment}
                                enter="ease-out duration-300"
                                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                                enterTo="opacity-100 translate-y-0 sm:scale-100"
                                leave="ease-in duration-200"
                                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            >
                                <Dialog.Panel
                                    className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-sm sm:p-6">
                                    <div>
                                        {/*<div*/}
                                        {/*    className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100">*/}
                                        {/*    <CheckIcon className="h-6 w-6 text-green-600" aria-hidden="true"/>*/}
                                        {/*</div>*/}
                                        <div className="mt-3 text-center sm:mt-5">
                                            <Dialog.Title as="h3"
                                                          className="text-base font-semibold leading-6 text-gray-900">
                                                {(userReward && userReward.reward) ?
                                                    <p>You are <span
                                                        className="font-bold text-xl">{userReward.reward.name}</span> status
                                                    </p>
                                                    : <p>You have no rewards</p>
                                                }
                                            </Dialog.Title>
                                            <div className="mt-2">
                                                <ul role="list"
                                                    className="mt-8 space-y-3 text-sm leading-6 text-gray-600">
                                                    {userReward && userReward.reward && userReward.reward.features && userReward.reward.features.map((feature) => (
                                                        <li key={feature} className="flex gap-x-3">
                                                            <CheckIcon className="h-6 w-5 flex-none text-pink-600"
                                                                       aria-hidden="true"/>
                                                            {feature}
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="mt-5 sm:mt-6">
                                        <button
                                            type="button"
                                            className="inline-flex w-full justify-center rounded-md bg-pink-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-pink-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-pink-600"
                                            onClick={() => setRewardOpen(false)}
                                        >
                                            Close
                                        </button>
                                    </div>
                                </Dialog.Panel>
                            </Transition.Child>
                        </div>
                    </div>
                </Dialog>
            </Transition.Root>

        </>
    )
}
